import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Stethoscope, Laptop, GraduationCap, HeartHandshake, ShieldCheck, Users } from "lucide-react";
import { api, mediaSrc, track } from "../../lib/api";
import { useSite, useSeo } from "../../lib/site";
import { ProgramCard, ClassCard, TestimonialCard, CtaBlock, Benefit, FaqList } from "../../components/site/Cards";
import LeadForm from "../../components/site/LeadForm";
import { EmptyState } from "../../components/common";

function Hero() {
  const { site } = useSite();
  const h = site.hero || {};
  const vref = useRef(null);
  const [videoOk, setVideoOk] = useState(true);
  const reduce = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  const src = h.video_media_id ? mediaSrc(`media:${h.video_media_id}`) : h.video_url;
  const poster = h.poster_media_id ? mediaSrc(`media:${h.poster_media_id}`) : h.poster_url;
  return (
    <section className="relative isolate flex min-h-[640px] items-center overflow-hidden bg-slate-950 lg:min-h-[720px]" data-testid="home-hero">
      {poster && <img src={poster} alt="" className="absolute inset-0 -z-20 h-full w-full object-cover" fetchPriority="high" />}
      {src && videoOk && !reduce && (
        <video ref={vref} className="absolute inset-0 -z-10 hidden h-full w-full object-cover sm:block" autoPlay muted loop playsInline preload="metadata" poster={poster}
          onError={() => setVideoOk(false)} data-testid="hero-video" aria-hidden>
          <source src={src} type="video/mp4" />
        </video>
      )}
      <div className="absolute inset-0 -z-10 bg-gradient-to-r from-slate-950/90 via-slate-950/65 to-slate-900/20" />
      <div className="container-x py-24">
        <div className="max-w-3xl fade-up">
          <p className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3.5 py-1.5 text-xs font-semibold text-white backdrop-blur" data-testid="hero-micro">
            <span className="h-2 w-2 animate-pulse rounded-full bg-red-500" />{h.micro}
          </p>
          <h1 className="mt-6 font-heading text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-7xl" data-testid="hero-headline">
            {(h.headline || "").replace("Healthcare.", "")}<span className="text-red-500">{(h.headline || "").includes("Healthcare.") ? "Healthcare." : ""}</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base text-slate-200 md:text-lg">{h.subheadline}</p>
          <div className="mt-9 flex flex-wrap gap-3">
            <Link to="/programs" onClick={() => track("cta_click", { label: "hero_explore" })} className="btn-primary" data-testid="hero-explore-btn">{h.cta_primary || "Explore Programs"} <ArrowRight className="h-4 w-4" /></Link>
            <Link to="/apply" onClick={() => track("cta_click", { label: "hero_apply" })} className="btn-outline border-white/30 bg-white text-slate-900" data-testid="hero-apply-btn">{h.cta_secondary || "Start Your Application"}</Link>
            <Link to="/classes" onClick={() => track("cta_click", { label: "hero_classes" })} className="inline-flex items-center gap-2 px-2 py-3 font-semibold text-white underline-offset-4 hover:underline" data-testid="hero-classes-btn">{h.cta_tertiary || "View Upcoming Classes"}</Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const { site, programs, programsLoaded } = useSite();
  const [classes, setClasses] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [faqs, setFaqs] = useState([]);
  useSeo({ title: "", description: site.seo_default_description, path: "/" });
  useEffect(() => {
    api.get("/public/classes").then((r) => setClasses(r.data)).catch(() => {});
    api.get("/public/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
    api.get("/public/faqs").then((r) => setFaqs(r.data.filter((f) => !f.program_id).slice(0, 5))).catch(() => {});
  }, []);
  return (
    <>
      <Hero />
      <section className="border-b border-slate-100 bg-white">
        <div className="container-x grid grid-cols-2 gap-6 py-8 text-center md:grid-cols-4">
          {[["30+ yrs", "Healthcare experience"], [`${programs.length || 6}`, "Career programs"], ["Hybrid", "Online + hands-on labs"], ["SpouseWorks", "Accepted for eligible programs"]].map(([a, b]) => (
            <div key={b}><p className="font-heading text-2xl font-extrabold text-slate-900 sm:text-3xl">{a}</p><p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{b}</p></div>
          ))}
        </div>
      </section>

      <section className="container-x py-20">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <p className="eyebrow">Why Healthcare Resources</p>
            <h2 className="mt-3 font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">Start your healthcare career with <span className="hl">real-world training</span></h2>
            <p className="mt-5 text-slate-600">Get <strong className="text-slate-900">hands-on healthcare training</strong> designed to help you build the skills, confidence, and credentials needed for your next career step — right here in <strong className="text-slate-900">Martinez and Augusta, GA</strong>.</p>
            <Link to="/programs" className="mt-7 inline-flex items-center gap-2 font-bold text-red-600" data-testid="why-explore-link">Explore Healthcare Programs <ArrowRight className="h-4 w-4" /></Link>
          </div>
          <div className="grid gap-8 sm:grid-cols-2 lg:col-span-7">
            <Benefit icon={Stethoscope} title="Hands-On Skills">Labs, skills check-offs and clinical or externship hours in real healthcare settings.</Benefit>
            <Benefit icon={Laptop} title="Flexible Learning">Hybrid options let you complete lectures online and come in for labs.</Benefit>
            <Benefit icon={GraduationCap} title="Experienced Instructors">Learn from healthcare professionals who bring real-world knowledge to every lesson.</Benefit>
            <Benefit icon={HeartHandshake} title="Support to Completion">From enrollment through certification guidance — with small class sizes.</Benefit>
          </div>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="container-x">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div><p className="eyebrow">Programs</p><h2 className="mt-3 font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">Healthcare programs in <span className="hl">Augusta, GA</span></h2></div>
            <Link to="/programs" className="btn-outline btn-sm" data-testid="home-all-programs">Compare all programs</Link>
          </div>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {programs.slice(0, 6).map((p, i) => <ProgramCard key={p.id} p={p} i={i} />)}
          </div>
          {programsLoaded && !programs.length && <EmptyState>Programs are being updated. Contact Admissions for current offerings.</EmptyState>}
        </div>
      </section>

      <section className="container-x py-20">
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <p className="eyebrow">Upcoming Classes</p>
            <h2 className="mt-3 font-heading text-3xl font-extrabold tracking-tight">Reserve your seat — <span className="hl">class sizes are small</span></h2>
            <p className="mt-4 text-slate-600">Seat availability updates in real time as students enroll.</p>
            <Link to="/classes" className="btn-dark mt-6" data-testid="home-view-classes">View full schedule</Link>
          </div>
          <div className="space-y-4 lg:col-span-8">
            {classes.slice(0, 4).map((c) => <ClassCard key={c.id} c={c} />)}
            {!classes.length && <EmptyState>No upcoming classes are currently available. Contact Admissions to discuss the next available start date.</EmptyState>}
          </div>
        </div>
      </section>

      <section className="bg-slate-900 py-20 text-white">
        <div className="container-x grid gap-10 lg:grid-cols-2 lg:items-center">
          <div>
            <p className="eyebrow text-red-400">Funding</p>
            <h2 className="mt-3 font-heading text-3xl font-extrabold sm:text-4xl">Military spouse? We accept <span className="text-red-400">SpouseWorks</span>.</h2>
            <p className="mt-4 text-slate-300">Choose SpouseWorks when you apply and our team will guide you through the next steps, or use Self Pay with secure Square checkout and payment plans. Funding approval is determined by the funding program.</p>
            <div className="mt-7 flex flex-wrap gap-3"><Link to="/funding" onClick={() => track("funding_inquiry")} className="btn-primary" data-testid="home-funding-btn">Explore funding options</Link></div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {[[ShieldCheck, "SpouseWorks", "Nursing Assistant, EKG, Medical Assistant, Phlebotomy"], [Users, "Self Pay", "Secure Square checkout, deposits & payment plans"]].map(([I, t, d]) => (
              <div key={t} className="rounded-2xl border border-slate-700 bg-slate-800/60 p-6"><I className="h-6 w-6 text-red-400" /><h3 className="mt-3 font-heading font-bold">{t}</h3><p className="mt-1 text-sm text-slate-400">{d}</p></div>
            ))}
          </div>
        </div>
      </section>

      {testimonials.length > 0 && (
        <section className="container-x py-20">
          <p className="eyebrow">Student Success</p>
          <h2 className="mt-3 font-heading text-3xl font-extrabold">What our students say</h2>
          <div className="mt-8 grid gap-6 md:grid-cols-3">{testimonials.slice(0, 3).map((t) => <TestimonialCard key={t.id} t={t} />)}</div>
        </section>
      )}

      <section className="container-x grid gap-12 py-20 lg:grid-cols-2">
        <div>
          <p className="eyebrow">Questions</p>
          <h2 className="mt-3 mb-8 font-heading text-3xl font-extrabold">Frequently asked questions</h2>
          <FaqList faqs={faqs} />
        </div>
        <LeadForm form="Homepage" compact title="Get program details & next start dates" />
      </section>
      <CtaBlock />
    </>
  );
}
